<template>
  <section class="preorder-page">
    <div class="page-header">
      <div>
        <h2>预下单列表</h2>
        <p>查看前台运价查询提交的预下单信息。</p>
      </div>
    </div>

    <div class="toolbar-card">
      <div class="toolbar-grid">
        <el-input
          v-model="filters.keyword"
          clearable
          placeholder="搜索预下单ID / 用户 / 邮箱 / 电话 / 航线信息"
          @keyup.enter="searchList"
        />
        <el-select v-model="filters.orderStatus" clearable placeholder="选择订单状态">
          <el-option label="待处理" :value="0" />
          <el-option label="处理中" :value="1" />
          <el-option label="已完成" :value="2" />
          <el-option label="已取消" :value="3" />
        </el-select>
      </div>
      <div class="toolbar-actions">
        <el-button type="primary" @click="searchList">查询</el-button>
        <el-button @click="resetFilters">重置</el-button>
        <TableColumnSettings table-key="account-preorder" :columns="preorderTableColumns" @update="columns => preorderConfiguredColumns = columns" />
      </div>
    </div>

    <div class="table-card">
      <el-table v-loading="loading" :data="list" border>
        <el-table-column
          v-for="column in visiblePreorderColumns"
          :key="column.key"
          :prop="column.prop"
          :label="column.label"
          :width="column.width"
          :min-width="column.minWidth"
          :show-overflow-tooltip="column.showOverflowTooltip"
        >
          <template #default="{ row }">
            <span v-if="column.key === 'submitTime'">{{ formatDateTime(row.submitTime) }}</span>
            <span v-else-if="column.key === 'containerTotalCount'">{{ getContainerTotal(row) || '-' }}</span>
            <span v-else-if="column.key === 'containerInfo'">{{ formatContainerInfo(row.containerInfo) }}</span>
            <span v-else-if="column.key === 'etd'">{{ formatDate(row.etd) }}</span>
            <span v-else-if="column.key === 'eta'">{{ formatDate(row.eta) }}</span>
            <el-tag v-else-if="column.key === 'orderStatus'" :type="statusTagType(row.orderStatus)">{{ row.orderStatusDesc || '-' }}</el-tag>
            <span v-else-if="column.key === 'operateTime'">{{ formatDateTime(row.operateTime) }}</span>
            <span v-else>{{ row[column.prop] || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="{ row }">
            <el-button v-if="row.orderStatus === 0" link type="primary" @click="updateStatus(row, 'process')">处理</el-button>
            <el-button v-if="row.orderStatus === 1" link type="success" @click="updateStatus(row, 'complete')">完成</el-button>
            <el-button
              v-if="row.orderStatus === 0 || row.orderStatus === 1"
              link
              type="danger"
              @click="deleteRow(row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <Pagination :page="page" :size="size" :total="total" @current-change="handlePageChange" />
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import * as Request from '@/assets/request'
import Pagination from '@/components/spagination'
import TableColumnSettings from '@/components/tableColumnSettings'
import { getVisibleColumns } from '@/utils/tableColumns'

const filters = reactive({
  keyword: '',
  orderStatus: null,
})

const list = ref([])
const loading = ref(false)
const page = ref(1)
const size = ref(10)
const total = ref(0)
const preorderTableColumns = [
  { key: 'preOrderId', prop: 'preOrderId', label: '预下单ID', minWidth: 130 },
  { key: 'submitTime', label: '提交时间', minWidth: 170 },
  { key: 'fullName', prop: 'fullName', label: '用户名', minWidth: 160, showOverflowTooltip: true },
  { key: 'email', prop: 'email', label: '联系邮箱', minWidth: 220, showOverflowTooltip: true },
  { key: 'contactPhone', prop: 'contactPhone', label: '联系电话', minWidth: 150 },
  { key: 'containerTotalCount', label: '预下单数量', width: 120 },
  { key: 'containerInfo', prop: 'containerInfo', label: '箱型箱量', minWidth: 180, showOverflowTooltip: true },
  { key: 'orderRemark', prop: 'orderRemark', label: '订单备注', minWidth: 220, showOverflowTooltip: true },
  { key: 'carrier', prop: 'carrier', label: '承运人', minWidth: 140, showOverflowTooltip: true },
  { key: 'portOfLoading', prop: 'portOfLoading', label: '起运港', minWidth: 140, showOverflowTooltip: true },
  { key: 'portOfDischarge', prop: 'portOfDischarge', label: '目的港', minWidth: 140, showOverflowTooltip: true },
  { key: 'vesselName', prop: 'vesselName', label: '船名', minWidth: 160, showOverflowTooltip: true },
  { key: 'voyage', prop: 'voyage', label: '航次', minWidth: 120, showOverflowTooltip: true },
  { key: 'routeCode', prop: 'routeCode', label: '航线代码', minWidth: 130, showOverflowTooltip: true },
  { key: 'voyageDuration', prop: 'voyageDuration', label: '航程', minWidth: 120, showOverflowTooltip: true },
  { key: 'etd', label: 'ETD', minWidth: 120 },
  { key: 'eta', label: 'ETA', minWidth: 120 },
  { key: 'orderStatus', label: '订单状态', width: 120 },
  { key: 'operateUser', prop: 'operateUser', label: '操作人', minWidth: 140, showOverflowTooltip: true },
  { key: 'operateTime', label: '操作时间', minWidth: 170 },
]
const preorderConfiguredColumns = ref(preorderTableColumns.map(column => ({ ...column })))
const visiblePreorderColumns = computed(() => getVisibleColumns(preorderConfiguredColumns.value))

const formatDateTime = (time) => {
  if (!time) {
    return '-'
  }
  const date = new Date(Number(time))
  if (Number.isNaN(date.getTime())) {
    return '-'
  }
  const pad = (value) => `${value}`.padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const formatDate = (time) => {
  if (!time) {
    return '-'
  }
  const date = new Date(Number(time))
  if (Number.isNaN(date.getTime())) {
    return '-'
  }
  const pad = (value) => `${value}`.padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const statusTagType = (status) => {
  if (status === 0) return 'warning'
  if (status === 1) return 'primary'
  if (status === 2) return 'success'
  if (status === 3) return 'info'
  return 'info'
}

const parseContainerText = (containerInfo) => {
  const trimmed = containerInfo.trim()
  if (!trimmed) {
    return []
  }
  const compactTextMatch = trimmed.match(/^\[(\d+)\*([^\]]+)\]$/)
  if (compactTextMatch) {
    return [{ number: Number(compactTextMatch[1]), type: compactTextMatch[2] }]
  }
  const legacyTextMatch = trimmed.match(/^(\d+)\*(.+)$/)
  if (legacyTextMatch && !trimmed.includes(',')) {
    return [{ number: Number(legacyTextMatch[1]), type: legacyTextMatch[2] }]
  }
  return trimmed.split(',').filter(Boolean).map(type => ({ type, number: 1 }))
}

const parseContainerInfo = (containerInfo) => {
  if (!containerInfo) {
    return []
  }
  if (Array.isArray(containerInfo)) {
    return containerInfo
  }
  if (typeof containerInfo === 'object') {
    return [containerInfo]
  }
  if (typeof containerInfo !== 'string') {
    return []
  }
  const trimmed = containerInfo.trim()
  if (!trimmed) {
    return []
  }
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)
      return Array.isArray(parsed) ? parsed : []
    } catch (error) {
      return parseContainerText(trimmed.replace(/^\[|\]$/g, ''))
    }
  }
  return parseContainerText(trimmed)
}

const getContainerTypeText = (item = {}) => {
  const type = item.type ?? item.containerType ?? item.name ?? item.label ?? item.value
  if (typeof type === 'string' || typeof type === 'number') {
    const normalized = String(type).trim()
    return normalized && !['{}', 'null', 'undefined'].includes(normalized) ? normalized : ''
  }
  if (type && typeof type === 'object') {
    return getContainerTypeText(type)
  }
  return ''
}

const getContainerNumber = (item = {}) => {
  const number = Number(item.number ?? item.quantity ?? item.qty ?? item.count)
  return Number.isFinite(number) && number > 0 ? number : 1
}

const formatContainerInfo = (containerInfo) => {
  const list = parseContainerInfo(containerInfo)
    .map(item => ({
      type: getContainerTypeText(item),
      number: getContainerNumber(item),
    }))
    .filter(item => item.type)
  if (!list.length) {
    return '-'
  }
  return list.map(item => `${item.number}*${item.type}`).join(', ')
}

const getContainerTotal = (row) => {
  if (typeof row.containerTotalCount === 'number') {
    return row.containerTotalCount
  }
  return parseContainerInfo(row.containerInfo).reduce((sum, item) => sum + getContainerNumber(item), 0)
}

const fetchList = async () => {
  loading.value = true
  const result = await Request.post({
    url: '/company/preorder/query',
    data: {
      keyword: filters.keyword?.trim() || undefined,
      orderStatus: filters.orderStatus ?? undefined,
      page: page.value,
      size: size.value,
    },
  })
  loading.value = false
  if (result?.status === 200) {
    list.value = result.data.list || []
    total.value = result.data.total || 0
  }
}

const searchList = () => {
  page.value = 1
  fetchList()
}

const resetFilters = () => {
  filters.keyword = ''
  filters.orderStatus = null
  searchList()
}

const handlePageChange = (current) => {
  page.value = current
  fetchList()
}

const updateStatus = async (row, action) => {
  const actionText = action === 'process' ? '处理' : '完成'
  try {
    await ElMessageBox.confirm(`确认将该预下单标记为“${actionText}”吗？`, `${actionText}预下单`, {
      type: 'warning',
    })
    const result = await Request.post({
      url: `/company/preorder/${action}`,
      data: {
        id: row.id,
      },
    })
    if (result?.status === 200) {
      ElMessage.success(`${actionText}成功`)
      fetchList()
    }
  } catch (error) {
    return
  }
}

const deleteRow = async (row) => {
  try {
    await ElMessageBox.confirm('确认删除该预下单吗？该操作会将订单状态更新为“已取消”。', '删除预下单', {
      type: 'warning',
    })
    const result = await Request.post({
      url: '/company/preorder/delete',
      data: {
        id: row.id,
      },
    })
    if (result?.status === 200) {
      ElMessage.success('删除成功')
      fetchList()
    }
  } catch (error) {
    return
  }
}

onMounted(() => {
  fetchList()
})
</script>

<style scoped lang="less">
.preorder-page {
  .page-header {
    margin-bottom: 20px;

    h2 {
      margin-bottom: 4px;
      font-size: 20px;
      line-height: 28px;
      color: #1f2937;
    }

    p {
      font-size: 13px;
      color: #6b7280;
    }
  }

  .toolbar-card,
  .table-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
  }

  .toolbar-card {
    padding: 12px 16px;
    margin-bottom: 12px;
  }

  .toolbar-grid {
    display: grid;
    grid-template-columns: minmax(260px, 1fr) 180px;
    gap: 12px;
    align-items: center;
  }

  .toolbar-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
  }

  .table-card {
    padding: 12px;
  }

  :deep(.el-form-item__label) {
    font-size: 13px;
    color: #4b5563;
    padding-bottom: 4px;
  }

  :deep(.el-input__inner),
  :deep(.el-input) {
    font-size: 13px;
  }

  @media (max-width: 900px) {
    .toolbar-grid {
      grid-template-columns: 1fr;
    }

    .toolbar-actions {
      flex-direction: column;
      align-items: stretch;
    }
  }
}
</style>
